/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { pageStateSelectors } from '../../../app/page/page-state/page-state.selectors';
import { agendaItemsPageSelectors } from '../../../app/page/page-storage/agenda-items/agenda-items.selectors';
import { agendaItemSelectors } from '../../../app/resource/agenda-item/agenda-item.selectors';
import { useSelect, useSelectWithParams } from '../../../utils/selector.utils';
import { PageLayout } from '../common/PageLayout';
import { LiveMeetingNavbar } from './LiveMeetingNavbar';
import { ISession } from '../../../app/resource/session/session.types';
import { moveStepAgenda } from '../../../app/page/page-storage/agenda-items/agenda-items.actions';
import slugify from 'react-slugify';
import { closeDialog, openDialog } from '../../../app/dialog/dialog.actions';
import { sessionSelectors } from '../../../app/resource/session/session.selectors';
import { authSelectors } from '../../../app/auth/auth.selectors';
import { IMeetingSimple } from '../../../app/resource/meeting-simple/meeting-simple.types';
import { meetingSimpleSelectors } from '../../../app/resource/meeting-simple/meeting-simple.selectors';
import { initialMeetingDashboardPage } from '../../../app/page/meeting-dashboard/meeting-dashboard.actions';
import { finishMeetingSession } from '../../../app/page/page-storage/meetings-simple/meetings-simple.actions';
import { sessionsPageSelectors } from '../../../app/page/page-storage/sessions/sessions.selectors';
import { hasUserElevatedPrivilegesDuringSession } from '../../../utils/user.utils';
import LiveMeetingView from './LiveMeetingView';
import useOutsideClick from '../../../utils/click.utils';
import { conclusionSelectors } from '../../../app/resource/conclusion/conclusion.selectors';
import { CONCLUSION } from '../../../app/resource/agenda-item/agenda-item.types';
import {
  getIncompleteRecapUserIds,
  hasAtLeastOneScoredMember,
  RECAP_NO_SCORE_ERROR_MESSAGE,
  RECAP_VALIDATION_ERROR_MESSAGE,
} from '../../../utils/conclusion.utils';
import { showRecapValidationErrors } from '../../../app/page/page-storage/conclusions/conclusions.actions';

export const LiveMeetingPage = () => {
  const { meetingId, sessionId } = useParams<{
    meetingId: IMeetingSimple['id'];
    sessionId: ISession['id'];
  }>();

  const agendaIndexFromPusher = useSelect(agendaItemsPageSelectors.selectCurrentAgenda);
  const agendaItemIds = useSelect(agendaItemsPageSelectors.selectAgendaItems)[meetingId];

  const agendaItemsSelected = useSelectWithParams(
    agendaItemSelectors.selectResourcesById,
    agendaItemIds ? agendaItemIds : [],
  );
  const agendaItems = useMemo(() => agendaItemsSelected.filter((agendaItem) => agendaItem?.checked === '1'), [
    agendaItemsSelected,
  ]);
  const conclusions = useSelect(conclusionSelectors.selectResourceList);
  const notAttendedIds = useSelect(sessionsPageSelectors.selectNotAttendedIds);

  const dispatch = useDispatch();
  const showLoader = useSelectWithParams(pageStateSelectors.selectPageInProgress, 'meetingDashboard');

  const history = useHistory();
  const scrollContainer = document.getElementById('scrollContainer');

  const path = history.location.pathname;

  useEffect(() => {
    if (scrollContainer && path) {
      scrollContainer.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [scrollContainer, path]);

  useEffect(() => {
    const unblock = history.block((location, action) => {
      if (action === 'POP') {
        // disable dialog on back/forward click
        return;
      }

      if (!location.pathname.includes('live-meeting') && !location.pathname.includes('/meeting-dashboard')) {
        dispatch(
          openDialog('confirmation', {
            title: 'Leave Meeting',
            message: 'Are you sure you want to leave the meeting?',
            cancelButton: 'Stay here',
            confirmButton: 'Leave',
            onConfirm: () => {
              dispatch(closeDialog('confirmation'));
              unblock();
              history.push(location.pathname);
            },
          }),
        );
        return false;
      }
    });

    return () => {
      unblock();
    };
  }, [history, dispatch]);

  useEffect(() => {
    dispatch(initialMeetingDashboardPage(meetingId));
  }, [dispatch, meetingId]);

  const session = useSelectWithParams(sessionSelectors.selectResourceById, sessionId);
  const currentStep = session?.currentStep ?? '0';

  const currentAgendaItem = useMemo(() => agendaItems[+currentStep], [currentStep, agendaItems]);

  useEffect(() => {
    if (
      !agendaIndexFromPusher ||
      agendaIndexFromPusher.meetingId !== meetingId ||
      agendaIndexFromPusher.sessionId !== sessionId
    ) {
      return;
    }
    const [, path, mId, sesId] = history.location.pathname.split('/');

    history.push(`/${path}/${mId}/${sesId}`);
  }, [agendaIndexFromPusher, history, meetingId, sessionId]);

  const nextAgendaItem = useMemo(() => agendaItems[+currentStep + 1], [currentStep, agendaItems]);
  const backAgendaItem = useMemo(() => agendaItems[+currentStep - 1], [currentStep, agendaItems]);
  const meeting = useSelectWithParams(meetingSimpleSelectors.selectResourceById, meetingId) as IMeetingSimple;
  const meetingMemberIds = useMemo(
    () =>
      meeting
        ? [...meeting.shareWithIds, ...meeting.administratorIds].filter((id, index, ids) => ids.indexOf(id) === index)
        : [],
    [meeting],
  );
  const hasConclusionStep = useMemo(() => agendaItems.some((agendaItem) => agendaItem?.keyName === CONCLUSION), [
    agendaItems,
  ]);
  const incompleteRecapUserIds = useMemo(
    () => getIncompleteRecapUserIds(meetingMemberIds, sessionId, conclusions, notAttendedIds),
    [meetingMemberIds, sessionId, conclusions, notAttendedIds],
  );
  const hasScoredMember = useMemo(() => hasAtLeastOneScoredMember(meetingMemberIds, sessionId, conclusions), [
    meetingMemberIds,
    sessionId,
    conclusions,
  ]);
  const isFinishBlockedByRecapValidation = hasConclusionStep && (incompleteRecapUserIds.length > 0 || !hasScoredMember);
  const recapValidationMessage =
    incompleteRecapUserIds.length > 0 ? RECAP_VALIDATION_ERROR_MESSAGE : RECAP_NO_SCORE_ERROR_MESSAGE;

  useEffect(() => {
    if (!isFinishBlockedByRecapValidation) {
      dispatch(showRecapValidationErrors(false));
    }
  }, [dispatch, isFinishBlockedByRecapValidation]);

  const openEndConfirmationModel = useCallback(() => {
    dispatch(
      openDialog('confirmation', {
        title: 'End meeting',
        message: 'Are you sure you want to end this meeting?',
        cancelButton: 'Cancel',
        confirmButton: 'End meeting',
        onConfirm: () => {
          dispatch(showRecapValidationErrors(false));
          dispatch(finishMeetingSession(meetingId, sessionId, false));
          history.push(`/meeting-dashboard/${meetingId}/${slugify(meeting.title, { delimiter: '-' })}`);
          dispatch(closeDialog('confirmation'));
        },
        onDeny: () => dispatch(closeDialog('confirmation')),
      }),
    );
  }, [dispatch, history, meeting, meetingId, sessionId]);

  const setNextAgendaItem = useCallback(() => {
    dispatch(moveStepAgenda({ meetingId, sessionId, index: `${+currentStep + 1}` }));
    const [, path, mId, sesId] = history.location.pathname.split('/');
    history.push(`/${path}/${mId}/${sesId}`);
  }, [currentStep, dispatch, history, meetingId, sessionId]);

  const setPreviousAgendaItem = useCallback(() => {
    dispatch(moveStepAgenda({ meetingId, sessionId, index: `${+currentStep - 1}` }));
    const [, path, mId, sesId] = history.location.pathname.split('/');
    history.push(`/${path}/${mId}/${sesId}`);
  }, [currentStep, dispatch, history, meetingId, sessionId]);

  const loggedUserId = useSelect(authSelectors.selectLoggedUser);

  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);

  const toggleCreateMenu = useCallback(() => {
    setIsCreateMenuOpen((prev) => !prev);
  }, []);

  const openGoalDialog = useCallback(() => {
    dispatch(openDialog('taskFormDialog', { meetingId }));
    setIsCreateMenuOpen(false);
  }, [dispatch, meetingId]);

  const openActionItemDialog = useCallback(() => {
    dispatch(openDialog('trelloReplacementFormDialog', { meetingId }));
    setIsCreateMenuOpen(false);
  }, [dispatch, meetingId]);

  const handleCreateMenuOutsideClick = useCallback(() => {
    if (isCreateMenuOpen) {
      setIsCreateMenuOpen(false);
    }
  }, [isCreateMenuOpen]);

  const createMenuRef = useOutsideClick(handleCreateMenuOutsideClick);

  if (loggedUserId === null) {
    return null;
  }
  const hasElevatedPrivileges = hasUserElevatedPrivilegesDuringSession(loggedUserId, session, meeting);

  return (
    <PageLayout
      showLoader={showLoader}
      smallSidebar
      externalLogicNavbar={
        currentAgendaItem ? (
          <LiveMeetingNavbar
            showLoader={showLoader}
            agendaItem={currentAgendaItem}
            meetingId={meetingId}
            sessionId={sessionId}
          />
        ) : undefined
      }
    >
      {!showLoader && (
        <>
          <div
            ref={createMenuRef}
            className={`dropdown plus_button_container${isCreateMenuOpen ? ' opened' : ''}`}
            style={{ position: 'fixed', bottom: 30, right: 30, zIndex: 1111 }}
          >
            <span
              className="plus_button themed-bg popupp"
              onClick={toggleCreateMenu}
              style={{ position: 'relative', bottom: 'auto', right: 'auto' }}
            >
              <i className="icon-plus"></i>
            </span>
            <ul
              className="dropdown-menu pointer"
              style={{ bottom: '100%', top: 'auto', right: 65, left: 'auto', marginBottom: 8 }}
            >
              <li onClick={openGoalDialog}>
                <span className="drop-item">Create Goal</span>
              </li>
              <li onClick={openActionItemDialog}>
                <span className="drop-item">Create Action Item</span>
              </li>
            </ul>
          </div>
          <LiveMeetingView currentAgendaItem={currentAgendaItem} meetingId={meetingId} sessionId={sessionId} />
          <div className="row mb-25">
            <div className="row mb-2" style={{ marginTop: '20px' }}>
              <div className="col-12">
                <div className="col-12">
                  {backAgendaItem && (
                    <div className="button-holder mr-2">
                      <button
                        className="btn darkGray-bg white m-0 w-auto"
                        title={`Back Agenda Item: ${backAgendaItem.title}`}
                        onClick={setPreviousAgendaItem}
                      >
                        <i className="icon icon-left-arrow mr-1 f-1"></i>
                        {`Previous Agenda Item: ${backAgendaItem.title}`}
                      </button>
                    </div>
                  )}
                  <div className="button-holder">
                    {nextAgendaItem && (
                      <button
                        className="btn white m-0 w-auto"
                        title={`Next Agenda Item: ${nextAgendaItem.title}`}
                        onClick={setNextAgendaItem}
                      >
                        <i className="icon icon-left-arrow icon-rotated mr-1 f-1"></i>
                        {`Next Agenda Item: ${nextAgendaItem.title}`}
                      </button>
                    )}
                    {!nextAgendaItem &&
                      hasElevatedPrivileges &&
                      (isFinishBlockedByRecapValidation ? (
                        <span
                          className="recap-finish-error-tooltip"
                          data-tooltip={recapValidationMessage}
                          onMouseEnter={() => dispatch(showRecapValidationErrors(true))}
                        >
                          <button className="btn white m-0 w-auto" disabled>
                            <i className="icon icon-left-arrow mr-1 f-1"></i>
                            {`Save and Finish Meeting`}
                          </button>
                        </span>
                      ) : (
                        <button
                          className="btn white m-0 w-auto"
                          title="Save and Finish Meeting"
                          onClick={openEndConfirmationModel}
                        >
                          <i className="icon icon-left-arrow mr-1 f-1"></i>
                          {`Save and Finish Meeting`}
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </PageLayout>
  );
};
